import { Download, ReceiptText } from "lucide-react";
import { useMemo } from "react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { WALLET_TOP_UPS_PAGE_SIZE } from "../constants/walletTopUps.constants";
import { useWalletTopUpReceipt } from "../hooks/useWalletTopUpReceipt";
import type { WalletTopUp } from "../types/walletTopUps.types";

export interface WalletTopUpsListProps {
  rows: WalletTopUp[];
  isLoading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}

/** The history twice, picked by CSS: a table from `lg` up, cards below it (design-system rule 7). */
export function WalletTopUpsList(props: WalletTopUpsListProps) {
  return (
    <>
      <div className="hidden lg:block">
        <WalletTopUpsTable {...props} />
      </div>
      <div className="lg:hidden">
        <WalletTopUpsCards {...props} />
      </div>
    </>
  );
}

function Amount({ topUp }: { topUp: WalletTopUp }) {
  return (
    <ColocaAmountDisplay
      value={topUp.amount}
      currency={topUp.currency}
      fractionDigits={minorUnitsFor(topUp.currency)}
      size="sm"
    />
  );
}

function ReceiptDownload({ topUp }: { topUp: WalletTopUp }) {
  const { t } = useTranslations("walletTopUps");
  const receipt = useWalletTopUpReceipt(topUp, { prefetch: false });
  return (
    <ColocaIconButton
      icon={Download}
      variant="ghost"
      size="sm"
      aria-label={t("receipt_download_for", { reference: topUp.reference })}
      disabled={receipt.busy !== null}
      onClick={() => void receipt.download()}
    />
  );
}

function EmptyHistory() {
  const { t } = useTranslations("walletTopUps");
  return (
    <ColocaEmpty
      icon={ReceiptText}
      title={t("empty_title")}
      description={t("empty_description")}
      surface="card"
    />
  );
}

function WalletTopUpsTable({ rows, isLoading, page, total, onPageChange }: WalletTopUpsListProps) {
  const { t, activeLanguage } = useTranslations("walletTopUps");
  const columns = useMemo<ColocaDataTableColumn<WalletTopUp>[]>(
    () => [
      {
        id: "date",
        header: t("col_date"),
        cell: (row) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {formatDateTime(row.created_at, { locale: activeLanguage })}
          </span>
        ),
      },
      {
        id: "organization",
        header: t("col_organization"),
        cellClassName: "max-w-60",
        cell: (row) => (
          <span className="block truncate font-medium text-foreground" title={row.organization_name ?? ""}>
            {row.organization_name ?? "—"}
          </span>
        ),
      },
      {
        id: "amount",
        header: t("col_amount"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) => <Amount topUp={row} />,
      },
      {
        id: "concept",
        header: t("col_concept"),
        cellClassName: "max-w-72",
        cell: (row) => (
          <div className="min-w-0">
            <span className="flex min-w-0 items-center gap-1.5">
              {row.kind === "incident_refund" && (
                <ColocaBadge variant="secondary" className="shrink-0 text-[10px]">
                  {t("kind_incident_refund")}
                </ColocaBadge>
              )}
              <span className="block truncate text-sm text-foreground" title={row.concept}>
                {row.concept}
              </span>
            </span>
            {row.internal_note && (
              <span className="block truncate text-xs text-muted-foreground" title={row.internal_note}>
                {row.internal_note}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "reference",
        header: t("col_reference"),
        cell: (row) => <span className="font-mono text-xs text-foreground">{row.reference}</span>,
      },
      {
        id: "by",
        header: t("col_by"),
        cellClassName: "max-w-56",
        cell: (row) => (
          <span className="block truncate text-xs text-muted-foreground" title={row.created_by_email ?? ""}>
            {row.created_by_email ?? "—"}
          </span>
        ),
      },
      {
        id: "receipt",
        header: <span className="sr-only">{t("col_receipt")}</span>,
        headerClassName: "w-12",
        cell: (row) => <ReceiptDownload topUp={row} />,
      },
    ],
    [activeLanguage, t],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={isLoading}
      skeletonRows={6}
      minWidth={1080}
      empty={<EmptyHistory />}
      footer={
        total > WALLET_TOP_UPS_PAGE_SIZE ? (
          <ColocaPagination
            page={page}
            perPage={WALLET_TOP_UPS_PAGE_SIZE}
            total={total}
            withSeparator={false}
            size="sm"
            onPageChange={onPageChange}
          />
        ) : undefined
      }
    />
  );
}

function WalletTopUpsCards({ rows, isLoading, page, total, onPageChange }: WalletTopUpsListProps) {
  const { t, activeLanguage } = useTranslations("walletTopUps");
  if (isLoading && rows.length === 0) {
    return (
      <div className="space-y-3" aria-busy="true">
        <ColocaSkeleton className="h-28 w-full rounded-xl" />
        <ColocaSkeleton className="h-28 w-full rounded-xl" />
      </div>
    );
  }
  if (rows.length === 0) return <EmptyHistory />;
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {row.organization_name ?? "—"}
                </p>
                <p className="font-mono text-[11px] text-muted-foreground">
                  {row.reference} · {formatDateTime(row.created_at, { locale: activeLanguage })}
                </p>
              </div>
              <ReceiptDownload topUp={row} />
            </div>
            <div className="mt-2 text-primary">
              <Amount topUp={row} />
            </div>
            <p className="mt-2 text-sm break-words text-foreground">
              {row.kind === "incident_refund" && (
                <ColocaBadge variant="secondary" className="mr-1.5 align-middle text-[10px]">
                  {t("kind_incident_refund")}
                </ColocaBadge>
              )}
              {row.concept}
            </p>
            {row.internal_note && (
              <p className="mt-1 text-xs break-words text-muted-foreground">
                {t("card_note", { note: row.internal_note })}
              </p>
            )}
            {row.created_by_email && (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {t("card_by", { email: row.created_by_email })}
              </p>
            )}
          </li>
        ))}
      </ul>
      {total > WALLET_TOP_UPS_PAGE_SIZE && (
        <ColocaPagination
          page={page}
          perPage={WALLET_TOP_UPS_PAGE_SIZE}
          total={total}
          size="sm"
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
